// src/components/login/login-page.tsx
import { useState, useEffect, FormEvent } from "react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Alert, AlertDescription } from "../ui/alert";
import { UpmodeLogo } from "./upmode-logo";
import { Eye, EyeOff, Mail, Lock, AlertCircle, Loader2, ArrowRight } from "lucide-react";
import { authApi, type LoginResponse } from "@/api/auth";
import { getSsoAvailability, startSsoLogin } from "@/api/sso";

/**
 * The login page now resolves to ONE of three branches based on
 * the response's mfa_state:
 *
 *   "none"            → onLoginSuccess(token) — same as before
 *   "setup_required"  → onMfaSetupRequired(setup_token, username, ...)
 *   "challenge"       → onMfaChallenge(challenge_token, username, ...)
 *
 * App.tsx renders the corresponding next page.
 */
interface LoginPageProps {
  onLoginSuccess: (token: string, payload: {
    username: string;
    is_admin: boolean;
    user_type: string;
    full_name?: string | null;
    rememberMe: boolean;
  }) => void;
  onMfaSetupRequired: (setup_token: string, payload: {
    username: string;
    is_admin: boolean;
    user_type: string;
    full_name?: string | null;
    rememberMe: boolean;
  }) => void;
  onMfaChallenge: (challenge_token: string, payload: {
    username: string;
    is_admin: boolean;
    user_type: string;
    full_name?: string | null;
    rememberMe: boolean;
  }) => void;

  onNavigateToSignup?: () => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;

  /** Seed an error message (e.g. a failed SSO redirect handed back by App). */
  initialError?: string | null;
}

export function LoginPage({
  onLoginSuccess,
  onMfaSetupRequired,
  onMfaChallenge,
  onNavigateToSignup,
  onNavigateToTerms,
  onNavigateToPrivacy,
  initialError,
}: LoginPageProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(initialError || "");

  const [ssoEnabled, setSsoEnabled] = useState(false);
  // Customer SSO connections are chosen by email domain, so the button needs
  // the email typed above before it knows which IdP to send the user to.
  const [ssoNeedsEmail, setSsoNeedsEmail] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    getSsoAvailability().then(({ enabled, needsEmail }) => {
      if (!alive) return;
      setSsoEnabled(enabled);
      setSsoNeedsEmail(needsEmail);
    });
    return () => { alive = false; };
  }, []);

  const handleSsoLogin = async () => {
    setErrorMsg("");
    const workEmail = email.includes("@") ? email : "";
    if (ssoNeedsEmail && !workEmail) {
      setErrorMsg("Enter your work email above, then choose Sign in with SSO.");
      return;
    }
    setSsoLoading(true);
    try {
      await startSsoLogin(workEmail);   // navigates away to the IdP on success
    } catch (err: any) {
      setSsoLoading(false);
      setErrorMsg(err?.message || "Couldn't start SSO sign-in. Please try again.");
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setIsLoading(true);

    try {
      const data: LoginResponse = await authApi.login({
        username: email.trim(),
        password,
        rememberMe,
      });

      const basePayload = {
        username:  data.username,
        is_admin:  !!data.is_admin,
        user_type: data.user_type || "basic",
        full_name: data.full_name,
        rememberMe,
      };

      if (data.mfa_state === "setup_required") {
        if (!data.setup_token) throw new Error("Server did not return a setup token.");
        onMfaSetupRequired(data.setup_token, basePayload);
        return;
      }

      if (data.mfa_state === "challenge") {
        if (!data.challenge_token) throw new Error("Server did not return a challenge token.");
        onMfaChallenge(data.challenge_token, basePayload);
        return;
      }

      // mfa_state === "none"
      if (!data.access_token) throw new Error("Server did not return an access token.");
      onLoginSuccess(data.access_token, basePayload);
    } catch (err: any) {
      const raw = String(err?.message || "");
      const isBadCreds =
        raw.includes("Invalid username or password") ||
        raw.toLowerCase().includes("401");

      setErrorMsg(
        isBadCreds
          ? "That email or password didn't match. Double-check and try again."
          : raw || "We couldn't sign you in right now. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        height: "100vh",
        backgroundColor: "var(--background)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "clamp(16px, 3vh, 28px) clamp(16px, 3vw, 24px)",
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          position: "absolute", inset: 0,
          background: `
            radial-gradient(circle at 20% 20%, var(--spectral-violet-5) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, var(--signal-blue-5) 0%, transparent 50%),
            radial-gradient(circle at 40% 60%, var(--alpha-teal-2) 0%, transparent 40%)
          `,
          opacity: 0.6, pointerEvents: "none",
        }}
      />

      <div style={{
        width: "100%", maxWidth: 640,
        maxHeight: "calc(100vh - 48px)",
        display: "flex", flexDirection: "column",
        justifyContent: "center", gap: "clamp(14px, 2.2vh, 22px)",
        position: "relative", zIndex: 1, overflowY: "auto",
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ display: "inline-flex", marginBottom: "clamp(10px, 2vh, 18px)" }}>
            <UpmodeLogo />
          </div>
          <h1 style={{
            fontFamily: "var(--font-sans)",
            fontSize: "var(--text-4xl)",
            fontWeight: "var(--font-weight-semibold)",
            color: "var(--foreground)",
            lineHeight: "var(--leading-tight)",
            letterSpacing: "-0.02em",
            margin: 0,
          }}>
            Welcome back
          </h1>
        </div>

        <div style={{
          backgroundColor: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: "16px",
          padding: "clamp(22px, 3.5vh, 44px) clamp(22px, 3.5vw, 44px)",
          boxShadow: "var(--elevation-lg)",
          position: "relative", overflow: "hidden",
        }}>
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: "4px",
            background: "linear-gradient(90deg, var(--spectral-violet), var(--signal-blue), var(--alpha-teal))",
            borderRadius: "16px 16px 0 0", opacity: 0.8,
          }}/>

          {errorMsg && (
            <Alert variant="destructive" style={{
              marginBottom: "16px",
              backgroundColor: "var(--infrared-coral-5)",
              border: "1px solid var(--infrared-coral)",
              borderRadius: "var(--radius)",
            }}>
              <AlertCircle style={{ width: 16, height: 16, color: "var(--infrared-coral)" }} />
              <AlertDescription style={{
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-sm)",
                color: "var(--infrared-coral)",
              }}>
                {errorMsg}
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div style={{ marginBottom: "16px" }}>
              <label htmlFor="email" style={{
                display: "block",
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-base)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--foreground)",
                marginBottom: "10px",
              }}>
                Email address
              </label>
              <div style={{ position: "relative" }}>
                <Mail style={{
                  position: "absolute", left: 18, top: "50%",
                  transform: "translateY(-50%)",
                  width: 20, height: 20,
                  color: "var(--muted-foreground)",
                  pointerEvents: "none",
                }}/>
                <Input
                  id="email" type="email" placeholder="you@company.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  required disabled={isLoading}
                  style={{
                    paddingLeft: 52, height: 54,
                    fontFamily: "var(--font-sans)",
                    fontSize: "var(--text-lg)",
                    borderRadius: "var(--radius)",
                    border: "1px solid var(--border)",
                    backgroundColor: "var(--input-background)",
                  }}
                />
              </div>
            </div>

            {/* Password */}
            <div style={{ marginBottom: "16px" }}>
              <label htmlFor="password" style={{
                display: "block",
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-base)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--foreground)",
                marginBottom: "10px",
              }}>
                Password
              </label>
              <div style={{ position: "relative" }}>
                <Lock style={{
                  position: "absolute", left: 18, top: "50%",
                  transform: "translateY(-50%)",
                  width: 20, height: 20,
                  color: "var(--muted-foreground)",
                  pointerEvents: "none",
                }}/>
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  required disabled={isLoading}
                  style={{
                    paddingLeft: 52, paddingRight: 52, height: 54,
                    fontFamily: "var(--font-sans)",
                    fontSize: "var(--text-lg)",
                    borderRadius: "var(--radius)",
                    border: "1px solid var(--border)",
                    backgroundColor: "var(--input-background)",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  style={{
                    position: "absolute", right: 18, top: "50%",
                    transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer",
                    padding: 4, color: "var(--muted-foreground)",
                  }}
                >
                  {showPassword
                    ? <EyeOff style={{ width: 20, height: 20 }} />
                    : <Eye style={{ width: 20, height: 20 }} />}
                </button>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "18px" }}>
              <Checkbox
                id="remember"
                checked={rememberMe}
                onCheckedChange={(v) => setRememberMe(v as boolean)}
                disabled={isLoading}
              />
              <label htmlFor="remember" style={{
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-base)",
                color: "var(--foreground)",
                cursor: "pointer",
              }}>
                Remember me
              </label>
            </div>

            <Button
              type="submit"
              disabled={isLoading || !email || !password}
              style={{
                width: "100%", height: 54,
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-lg)",
                fontWeight: "var(--font-weight-semibold)",
                background: isLoading || !email || !password ? "var(--muted)" : "var(--alpha-teal)",
                color: "white", border: "none",
                borderRadius: "var(--radius)",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              }}
            >
              {isLoading ? (
                <>
                  <Loader2 style={{ width: 20, height: 20 }} className="animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight style={{ width: 20, height: 20 }} />
                </>
              )}
            </Button>

            {ssoEnabled && (
              <>
                <div style={{
                  display: "flex", alignItems: "center", gap: 12, margin: "14px 0",
                }}>
                  <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
                  <span style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "var(--text-xs)",
                    color: "var(--muted-foreground)",
                  }}>
                    or
                  </span>
                  <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
                </div>

                <Button
                  type="button"
                  onClick={handleSsoLogin}
                  disabled={isLoading || ssoLoading}
                  style={{
                    width: "100%", height: 54,
                    fontFamily: "var(--font-sans)",
                    fontSize: "var(--text-lg)",
                    fontWeight: "var(--font-weight-semibold)",
                    background: "var(--card)",
                    color: "var(--foreground)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius)",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  }}
                >
                  {ssoLoading ? (
                    <>
                      <Loader2 style={{ width: 20, height: 20 }} className="animate-spin" />
                      Redirecting...
                    </>
                  ) : (
                    "Sign in with SSO"
                  )}
                </Button>
              </>
            )}
          </form>

          <div style={{
            textAlign: "center", marginTop: 14,
            fontFamily: "var(--font-sans)",
            fontSize: "var(--text-xs)",
            color: "var(--muted-foreground)",
            lineHeight: "var(--leading-relaxed)",
          }}>
            By signing in, you agree to our{" "}
            <button
              onClick={(e) => { e.preventDefault(); onNavigateToTerms?.(); }}
              style={{ background: "none", border: "none", color: "var(--signal-blue)",
                       cursor: "pointer", padding: 0,
                       fontFamily: "var(--font-sans)", fontSize: "var(--text-xs)" }}
            >
              Terms of Service
            </button>{" "}
            and{" "}
            <button
              onClick={(e) => { e.preventDefault(); onNavigateToPrivacy?.(); }}
              style={{ background: "none", border: "none", color: "var(--signal-blue)",
                       cursor: "pointer", padding: 0,
                       fontFamily: "var(--font-sans)", fontSize: "var(--text-xs)" }}
            >
              Privacy Policy
            </button>
            .
          </div>
        </div>

        {onNavigateToSignup && (
          <div style={{ textAlign: "center",
                        fontFamily: "var(--font-sans)",
                        fontSize: "var(--text-sm)",
                        color: "var(--muted-foreground)" }}>
            Don't have an account?{" "}
            <button
              onClick={onNavigateToSignup}
              disabled={isLoading}
              style={{ background: "none", border: "none", color: "var(--alpha-teal)",
                       fontWeight: "var(--font-weight-semibold)", cursor: "pointer", padding: 0 }}
            >
              Sign up
            </button>
          </div>
        )}
      </div>
    </div>
  );
}